import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import classnames from 'classnames';
import regexpEscape from 'escape-string-regexp';
import { forwardRef, Fragment, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { Button } from './button';
import { IconChevronDown, IconX } from './icons';
const matches = (text, pattern) => {
    if (!pattern)
        return text;
    let next = 0;
    let match = null;
    const elements = [];
    pattern.lastIndex = 0;
    while ((match = pattern.exec(text))) {
        if (next < match.index) {
            elements.push(text.slice(next, match.index));
        }
        elements.push(_jsx("em", { children: match[0] }, match.index));
        next = match.index + match[0].length;
    }
    elements.push(text.slice(next));
    return elements;
};
// Convert numbers to strings for comparison.
const numberToString = (value) => {
    if (value == null)
        return value;
    if (Number.isFinite(value))
        return value.toString();
    return value;
};
// Compare values after coercing number values to strings.
const valueMatches = (a, b) => {
    return numberToString(a) === numberToString(b);
};
export const Chosen = forwardRef(function Chosen(props, ref) {
    const { id = undefined, choices: choicesProp = [], className: classNameProp, disabled, groupLabel = false, name, onChange, onOpen, onClose, includeClearSelection = false, placeholder = '', selectionText = (choice) => {
        if (groupLabel) {
            return (_jsxs(_Fragment, { children: [_jsx("div", { className: "pds-text-sm pds-color-muted", children: choice.group?.name }), _jsx("div", { children: choice.text })] }));
        }
        else {
            return choice.text;
        }
    }, defaultValue = null, select = false, downLeft = false, required = false, small = false, value: valueProp, limit, ...rest } = props;
    const inputRef = useRef(null);
    const buttonRef = useRef(null);
    const requiredInputRef = useRef(null);
    const highlightedRef = useRef(null);
    // We need to allow form submit when pressing Enter, but preventDefault when
    // clicking the button. To differentiate between those events, we watch for
    // mousedown and keydown to decide if a click event was triggered by pressing
    // enter.
    const ignoreClick = useRef(false);
    const uid = useId();
    const [isOpen, setIsOpen] = useState(false);
    const [isFading, setIsFading] = useState(false);
    const [query, setQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const focus = () => {
        buttonRef.current?.focus();
    };
    const open = () => {
        if (!disabled) {
            setIsOpen(true);
            setIsFading(false);
            setQuery('');
        }
    };
    useImperativeHandle(ref, () => ({ focus, open }));
    const [valueState, setValue] = useState(defaultValue);
    // Value prop takes precedence over state & defaultValue
    const value = 'value' in props ? valueProp : valueState;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function isChoiceArray(items) {
        return items.every((x) => 'text' in x);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function isGroupArray(items) {
        return items.every((x) => 'name' in x);
    }
    // Were groups passed in?
    const groups = isGroupArray(choicesProp);
    // If choices are grouped, flatten them into one list
    const choices = useMemo(() => {
        let choices = [];
        if (isChoiceArray(choicesProp)) {
            choices = [{ name: null, choices: choicesProp }];
        }
        else if (isGroupArray(choicesProp)) {
            choices = choicesProp;
        }
        else {
            throw new Error('choices must be ChosenChoice[] | ChosenGroup[]');
        }
        return choices.flatMap(({ choices, name }, id) => choices.map((choice) => {
            const group = { id, name };
            return { ...choice, group };
        }));
    }, [choicesProp]);
    const choiceIds = useMemo(() => {
        return new Map(choices.map((choice, index) => [choice, index]));
    }, [choices]);
    const selectedChoice = useMemo(() => {
        return choices.find((choice) => {
            return valueMatches(choice.value, value);
        });
    }, [choices, value]);
    const selectedValue = useMemo(() => {
        return selectedChoice ? selectedChoice.value : null;
    }, [selectedChoice]);
    const pattern = useMemo(() => {
        return query ? new RegExp(regexpEscape(query), 'ig') : null;
    }, [query]);
    const filtered = useMemo(() => {
        if (!pattern)
            return choices;
        return choices.filter(({ group, text }) => {
            pattern.lastIndex = 0;
            if (groups && group && group.name != null) {
                return pattern.test(text) || (group && pattern.test(group.name));
            }
            else {
                return pattern.test(text);
            }
        });
    }, [choices, groups, pattern]);
    const limited = useMemo(() => {
        if (limit == null) {
            return filtered;
        }
        else {
            return filtered.slice(0, limit);
        }
    }, [limit, filtered]);
    // Notify listeners
    useEffect(() => {
        if (isOpen) {
            onOpen?.();
        }
        else {
            onClose?.();
        }
    }, [isOpen, onClose, onOpen]);
    // Set focus and set highlighted index when opened
    useEffect(() => {
        if (!isOpen)
            return;
        inputRef.current?.focus();
        const selectedIndex = limited.findIndex((choice) => valueMatches(choice.value, selectedValue));
        setHighlightedIndex(selectedIndex === -1 ? 0 : selectedIndex);
    }, [limited, isOpen, selectedValue, inputRef]);
    // The button is first rendered with tabindex -1 to prevent it from being
    // focused when pressing shift-tab. Once focus has changed, we can remove the
    // attribute.
    useEffect(() => {
        if (isOpen) {
            buttonRef.current?.setAttribute('aria-hidden', 'true');
            buttonRef.current?.setAttribute('tabindex', '-1');
        }
        else {
            setTimeout(() => {
                buttonRef.current?.removeAttribute('aria-hidden');
                buttonRef.current?.removeAttribute('tabindex');
            }, 0);
        }
    }, [isOpen]);
    // Scroll to the highlighted item
    useEffect(() => {
        const element = highlightedRef.current;
        const parent = element?.parentElement;
        if (!element || !parent)
            return;
        const min = element.offsetTop;
        const max = element.offsetTop + element.offsetHeight - parent.offsetHeight;
        if (highlightedIndex === 0) {
            // The group name should be visible if the first choice is highlighted.
            parent.scrollTop = 0;
        }
        else if (highlightedIndex === limited.length - 1) {
            // The limit message should be visible if the last choice is highlighted.
            parent.scrollTop = parent.scrollHeight - parent.clientHeight;
        }
        else {
            parent.scrollTop = Math.max(Math.min(parent.scrollTop, min), max);
        }
    }, [highlightedIndex, isOpen, limited.length]);
    const onAnimationEnd = (event) => {
        if (event.animationName === 'fadeout') {
            setIsOpen(false);
            setQuery('');
        }
    };
    const close = useCallback(() => {
        setIsFading(true);
    }, []);
    // The dropdown is never open when disabled
    useEffect(() => {
        if (disabled)
            close();
    }, [disabled, close]);
    const move = (n) => {
        setHighlightedIndex(Math.min(limited.length - 1, Math.max(0, highlightedIndex + n)));
    };
    const choose = (newValue = null) => {
        setValue(newValue);
        close();
        if (onChange)
            onChange(newValue);
    };
    const onChangeQuery = ({ target }) => {
        open();
        setQuery(target.value);
        setHighlightedIndex(0);
    };
    const clearSelection = () => {
        choose();
    };
    const onButtonKeyDown = (event) => {
        // Skip when already open, default prevented, or meta key pressed.
        if (isOpen || event.defaultPrevented || event.metaKey)
            return;
        // ArrowUp and ArrowDown opens the dropdown
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            open();
        }
        // Normal characters open the chosen and populate the query
        if (event.key.length === 1) {
            open();
            setQuery(event.key);
            // Make sure the event doesn't bubble up and add the character twice.
            // https://github.com/harvesthq/harvestapp/issues/27462
            event.preventDefault();
        }
    };
    const onButtonClick = (event) => {
        // Submit for keyboard clicks, but not mouse clicks.
        if (ignoreClick.current) {
            event.preventDefault();
        }
        // When pressing enter in a separate form field, the first button element
        // in the form will receive a click event. If the default is prevented then
        // the form will not submit. As a result, we should make sure to not ignore
        // subsequent clicks.
        ignoreClick.current = false;
    };
    const onButtonMouseDown = (event) => {
        // Don't respond to right-click events
        if (event.button === 2)
            return;
        // Prevent default so that we don't steal focus back from the search input
        event.preventDefault();
        ignoreClick.current = true;
        open();
    };
    const onKeyDown = (event) => {
        const item = limited[highlightedIndex];
        switch (event.key) {
            case 'Escape':
                // When the dropdown is open, pressing Escape should close it but not
                // cause a dialog to close.
                event.stopPropagation();
                setTimeout(focus, 0);
                close();
                break;
            case 'ArrowUp':
            case 'ArrowDown':
                event.preventDefault();
                move(event.key === 'ArrowUp' ? -1 : 1);
                break;
            case 'Tab':
            case 'Enter':
                if (event.key === 'Enter') {
                    event.preventDefault();
                    setTimeout(focus, 0);
                }
                if (item)
                    choose(item.value);
                // We unset required to hide the validation bubble, but if the user
                // submits the form by pressing enter, we want to set it again before
                // the submit event.
                if (requiredInputRef.current) {
                    requiredInputRef.current.required = true;
                }
                break;
        }
    };
    const renderResults = () => {
        if (!isOpen)
            return null;
        let previousGroupId = null;
        return (_jsxs("ul", { id: `chosen-results-${uid}`, className: "pds-chosen-results", role: "listbox", tabIndex: isFading ? -1 : undefined, children: [limited.map((choice, index) => {
                    const { text: choiceText, value: choiceValue, group, ...rest } = choice;
                    const ref = index === highlightedIndex ? highlightedRef : null;
                    const id = `chosen-result-${uid}-${choiceIds.get(choice)}`;
                    const className = classnames('pds-chosen-result', {
                        'pds-chosen-highlighted': index === highlightedIndex,
                        'pds-chosen-group-option': !!groups,
                    });
                    // onMouseEnter is fired when scrolling, so we use onMouseMove to
                    // capture only events when the mouse position has changed.
                    const onMouseMove = () => setHighlightedIndex(index);
                    const onMouseDown = () => {
                        choose(choiceValue);
                        setTimeout(focus, 0);
                    };
                    let groupItem = null;
                    if (groups && group) {
                        // If the group has changed, include a group item.
                        if (group.name && group.id !== previousGroupId) {
                            groupItem = (_jsx("li", { className: "pds-chosen-result-group", children: matches(group.name, pattern) }));
                        }
                        previousGroupId = group.id;
                    }
                    return (_jsxs(Fragment, { children: [groupItem, _jsx("li", { ref: ref, id: id, className: className, onMouseMove: onMouseMove, onMouseDown: onMouseDown, role: "option", ...rest, children: matches(choiceText, pattern) })] }, choiceValue));
                }), limit != null && filtered.length > limit && (_jsxs("li", { className: "pds-chosen-and-more", children: ["\u2026and ", filtered.length - limit, " more"] }))] }));
    };
    const renderSelection = () => {
        const className = classnames('pds-button', 'pds-chosen-button', {
            'pds-button-selected': !select && isOpen,
            'pds-button-sm': select && small,
            'pds-chosen-blank': !selectedChoice,
        });
        const selection = selectedChoice && selectionText(selectedChoice);
        return (_jsxs(Button, { id: id, ref: buttonRef, disabled: disabled, className: className, onKeyDownCapture: onButtonKeyDown, onMouseDown: onButtonMouseDown, onClick: onButtonClick, type: "submit", ...rest, children: [_jsx("div", { className: "pds-chosen-selection", children: selection || placeholder }), _jsx(IconChevronDown, { className: "pds-flex-no-shrink", "aria-hidden": "true" })] }));
    };
    const className = classnames(classNameProp, 'pds-chosen pds-chosen-container', {
        'pds-chosen-open': isOpen && !isFading,
        'pds-chosen-with-label': groupLabel && selectedChoice,
        'pds-chosen-select': select,
        'pds-chosen-menu': !select,
    });
    const dropClass = classnames('pds-chosen-dropdown', {
        'pds-chosen-left': !select && downLeft,
        'pds-chosen-right': !select && !downLeft,
    });
    const activeId = choiceIds.get(limited[highlightedIndex]);
    const renderInput = () => {
        if (disabled)
            return null;
        if (isFading) {
            return (_jsx("div", { className: "pds-input pds-chosen-input", children: _jsx("span", { className: classnames({ 'pds-text-muted': !query }), children: query || 'Search…' }) }));
        }
        return (_jsx("input", { ref: inputRef, type: "text", spellCheck: false, autoComplete: "off", value: query, onBlur: close, onChange: onChangeQuery, onKeyDown: onKeyDown, className: "pds-input pds-chosen-input", placeholder: "Search\u2026", role: "combobox", "aria-owns": `chosen-results-${uid}`, "aria-activedescendant": `chosen-result-${uid}-${activeId}`, tabIndex: -1 }));
    };
    const renderRequiredInput = () => {
        if (disabled || !required)
            return null;
        return (_jsx("input", { onChange: () => { }, ref: requiredInputRef, onFocus: focus, value: selectedValue || '', type: "text", required: !isOpen, tabIndex: -1, style: {
                // Ensure correct positioning of validation popup
                height: '100%',
                position: 'absolute',
                pointerEvents: 'none',
                opacity: 0,
            }, "aria-hidden": "true" }));
    };
    function renderValue() {
        if (name == null)
            return null;
        return (_jsx("input", { type: "hidden", name: name, value: selectedValue || '', disabled: disabled }));
    }
    function renderClear() {
        if (!includeClearSelection || !selectedChoice)
            return null;
        return (_jsx("div", { className: "pds-chosen-clear-selection-container", children: _jsxs(Button, { className: "pds-chosen-clear-selection", onMouseDown: clearSelection, children: [_jsx(IconX, { width: "13", height: "13" }), "Clear filter"] }) }));
    }
    function renderDropdown() {
        if (!isOpen)
            return null;
        return (_jsx("div", { className: classnames('pds-chosen-backdrop', {
                'pds-fadeout': isFading,
            }), onAnimationEnd: onAnimationEnd, children: _jsxs("div", { className: dropClass, children: [_jsx("div", { className: "pds-chosen-search", children: renderInput() }), limited.length ? (renderResults()) : (_jsxs("div", { className: "pds-chosen-no-results", children: ["No results match ", query] })), renderClear()] }) }));
    }
    return (_jsxs(Fragment, { children: [renderValue(), _jsxs("div", { className: className, children: [renderRequiredInput(), renderSelection(), renderDropdown()] })] }));
});
